/**
 * Visual + rule metadata for every Potato Pirates card type.
 * Colours reference CSS variables so they adapt to dark / light mode automatically.
 * Action cards = green (--pp-action-*), Control cards = orange (--pp-control-*).
 */
import { Flame, Hammer, Zap, Repeat, RefreshCw, GitBranch, type LucideIcon } from 'lucide-react'
import { ACTION_DAMAGE, CONTROL_PARAMS, type ActionType, type ControlType, type CardType } from './engine'

export interface CardDef {
  type: CardType
  kind: 'action' | 'control'
  label: string
  icon: LucideIcon
  bg: string
  border: string
  accent: string
  text: string
  description: string
  damage?: number
  paramOptions?: number[]
  nestedSlots?: number
}

const ACTION: Pick<CardDef, 'bg' | 'border' | 'accent' | 'text'> = {
  bg: 'var(--pp-action-bg)',
  border: 'var(--pp-action-border)',
  accent: 'var(--pp-action-accent)',
  text: 'var(--pp-action-text)',
}

const CONTROL: Pick<CardDef, 'bg' | 'border' | 'accent' | 'text'> = {
  bg: 'var(--pp-control-bg)',
  border: 'var(--pp-control-border)',
  accent: 'var(--pp-control-accent)',
  text: 'var(--pp-control-text)',
}

export const ACTION_DEFS: Record<ActionType, CardDef> = {
  roast: {
    type: 'roast', kind: 'action', label: 'Roast', icon: Flame, ...ACTION,
    description: 'Destroy 1 potato on an enemy ship.',
    damage: ACTION_DAMAGE.roast,
  },
  mash: {
    type: 'mash', kind: 'action', label: 'Mash', icon: Hammer, ...ACTION,
    description: 'Destroy 2 potatoes on an enemy ship.',
    damage: ACTION_DAMAGE.mash,
  },
  fry: {
    type: 'fry', kind: 'action', label: 'Fry', icon: Zap, ...ACTION,
    description: 'Destroy 3 potatoes on an enemy ship.',
    damage: ACTION_DAMAGE.fry,
  },
}

export const CONTROL_DEFS: Record<ControlType, CardDef> = {
  for: {
    type: 'for', kind: 'control', label: 'For', icon: Repeat, ...CONTROL,
    description: 'Repeat the action cards inside a fixed number of times.',
    paramOptions: CONTROL_PARAMS.for,
    nestedSlots: 1,
  },
  while: {
    type: 'while', kind: 'control', label: 'While', icon: RefreshCw, ...CONTROL,
    description: 'Repeat the action cards while the target ship has more than N potatoes.',
    paramOptions: CONTROL_PARAMS.while,
    nestedSlots: 1,
  },
  ifelse: {
    type: 'ifelse', kind: 'control', label: 'If / Else', icon: GitBranch, ...CONTROL,
    description:
      'For every enemy ship (not your own): if it has N or fewer potatoes, play the IF action on it, otherwise play the ELSE action.',
    paramOptions: CONTROL_PARAMS.ifelse,
    nestedSlots: 2,
  },
}

export const CARD_DEFS: Record<CardType, CardDef> = { ...ACTION_DEFS, ...CONTROL_DEFS }
export const ACTION_TYPES: ActionType[] = ['roast', 'mash', 'fry']
export const CONTROL_TYPES: ControlType[] = ['for', 'while', 'ifelse']
export const ALL_CARD_TYPES: CardType[] = [...ACTION_TYPES, ...CONTROL_TYPES]

export function getCardDef(type: CardType): CardDef {
  return CARD_DEFS[type]
}

export function controlParamLabel(control: ControlType, param: number): string {
  switch (control) {
    case 'for':    return `repeat × ${param}`
    case 'while':  return `while > ${param} potatoes`
    case 'ifelse': return `if ≤ ${param} potatoes`
  }
}
